// Step 1 do wizard: dados básicos do plano (sem entitlements).
import type {
  BillingCycleSchema,
  CreatePlanInput,
  PlanStatusSchema,
  PlanTypeSchema,
} from '@control-center/shared';

import { Field, Input, Select, Textarea } from '../../../components/clients/form-primitives';
import { PillButton } from '../../../components/clients/PillButton';

interface Step1DataProps {
  data: CreatePlanInput;
  onPatch: (patch: Partial<CreatePlanInput>) => void;
  onNext: () => void;
  onCancel: () => void;
}

const TYPE_OPTIONS: { value: PlanTypeSchema; label: string }[] = [
  { value: 'single_saas', label: 'Single SaaS (1 entitlement)' },
  { value: 'bundle', label: 'Bundle (2+ entitlements)' },
  { value: 'service_only', label: 'Service-only (0 entitlements)' },
];

const STATUS_OPTIONS: { value: PlanStatusSchema; label: string }[] = [
  { value: 'ativo', label: 'Ativo' },
  { value: 'oculto', label: 'Oculto' },
  { value: 'legado', label: 'Legado' },
];

const BILLING_OPTIONS: { value: BillingCycleSchema; label: string }[] = [
  { value: 'mensal', label: 'Mensal' },
  { value: 'trimestral', label: 'Trimestral' },
  { value: 'semestral', label: 'Semestral' },
  { value: 'anual', label: 'Anual' },
  { value: 'pagamento_unico', label: 'Pagamento único' },
];

export function Step1Data({ data, onPatch, onNext, onCancel }: Step1DataProps): JSX.Element {
  const slugValid = data.slug !== '' && /^[a-z0-9_-]+$/.test(data.slug);
  const nameValid = data.name.trim().length >= 1;
  const canAdvance = slugValid && nameValid;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Nome" required error={!nameValid ? 'Nome obrigatório.' : undefined}>
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              aria-describedby={describedBy}
              placeholder="Owl Suite Growth"
              value={data.name}
              onChange={(e) => onPatch({ name: e.target.value })}
            />
          )}
        </Field>

        <Field
          label="Slug"
          required
          hint="lowercase, hífen, underscore"
          error={data.slug !== '' && !slugValid ? 'Formato inválido.' : undefined}
        >
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              aria-describedby={describedBy}
              placeholder="owl-suite-growth"
              value={data.slug}
              onChange={(e) => onPatch({ slug: e.target.value })}
            />
          )}
        </Field>
      </div>

      <Field label="Descrição">
        {({ inputId }) => (
          <Textarea
            id={inputId}
            rows={3}
            placeholder="Descrição comercial do plano…"
            value={data.description ?? ''}
            onChange={(e) => onPatch({ description: e.target.value })}
          />
        )}
      </Field>

      <Field
        label="Destaques do checkout"
        hint="Um por linha (máx. 6, 120 caracteres cada). Aparecem no resumo do checkout público. Vazio = textos padrão."
      >
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            aria-describedby={describedBy}
            rows={4}
            placeholder={'Agenda ilimitada\nLembretes por WhatsApp\nSuporte incluso'}
            value={(data.checkout_highlights ?? []).join('\n')}
            onChange={(e) => onPatch({ checkout_highlights: e.target.value.split('\n') })}
          />
        )}
      </Field>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Tipo" required>
          {({ inputId }) => (
            <Select
              id={inputId}
              value={data.type}
              onChange={(e) => onPatch({ type: e.target.value as PlanTypeSchema })}
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Status" required>
          {({ inputId }) => (
            <Select
              id={inputId}
              value={data.status}
              onChange={(e) => onPatch({ status: e.target.value as PlanStatusSchema })}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <fieldset className="border-t border-line pt-4">
        <legend className="mb-3 text-[12px] font-medium uppercase tracking-wider text-muted-3">
          Precificação
        </legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Preço mensal (R$)" required>
            {({ inputId }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                value={data.monthly_price ?? 0}
                onChange={(e) => onPatch({ monthly_price: Number(e.target.value) })}
              />
            )}
          </Field>
          <Field label="Preço anual (R$)" hint="Opcional.">
            {({ inputId }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                value={data.annual_price ?? ''}
                onChange={(e) =>
                  onPatch({ annual_price: e.target.value === '' ? null : Number(e.target.value) })
                }
              />
            )}
          </Field>
          <Field label="Setup fee (R$)">
            {({ inputId }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                value={data.setup_fee ?? 0}
                onChange={(e) => onPatch({ setup_fee: Number(e.target.value) })}
              />
            )}
          </Field>
          <Field label="Preço promocional (R$)">
            {({ inputId }) => (
              <Input
                id={inputId}
                type="number"
                step="0.01"
                min="0"
                value={data.promo_price ?? ''}
                onChange={(e) =>
                  onPatch({ promo_price: e.target.value === '' ? null : Number(e.target.value) })
                }
              />
            )}
          </Field>
          <Field label="Ciclo de cobrança" required>
            {({ inputId }) => (
              <Select
                id={inputId}
                value={data.billing_cycle}
                onChange={(e) => onPatch({ billing_cycle: e.target.value as BillingCycleSchema })}
              >
                {BILLING_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Trial (dias)">
            {({ inputId }) => (
              <Input
                id={inputId}
                type="number"
                min="0"
                step="1"
                value={data.trial_days ?? 0}
                onChange={(e) => onPatch({ trial_days: Number(e.target.value) })}
              />
            )}
          </Field>
        </div>

        <div className="mt-3 flex flex-col gap-2 text-[13px]">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.requires_card_for_trial ?? false}
              onChange={(e) => onPatch({ requires_card_for_trial: e.target.checked })}
            />
            <span>Trial exige cartão</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.blocks_repeat_trial ?? true}
              onChange={(e) => onPatch({ blocks_repeat_trial: e.target.checked })}
            />
            <span>Bloquear trial repetido (por documento)</span>
          </label>
        </div>
      </fieldset>

      {/* Fase 2 — NFS-e auto (DR-036) */}
      <fieldset className="rounded-md border border-line p-4">
        <legend className="px-2 text-[12px] uppercase tracking-wider text-muted">
          Fiscal — NFS-e
        </legend>
        <div className="flex flex-col gap-3 text-[13px]">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.auto_emit_nf ?? false}
              onChange={(e) => onPatch({ auto_emit_nf: e.target.checked })}
            />
            <span>Emitir NF automaticamente ao receber pagamento</span>
          </label>
          {(data.auto_emit_nf ?? false) && (
            <Field label="Descrição do serviço (vai pra discriminação da NF, 10-500 caracteres)">
              {({ inputId, describedBy }) => (
                <textarea
                  id={inputId}
                  aria-describedby={describedBy}
                  className="w-full rounded border border-line bg-bg-soft p-2 text-[13px]"
                  rows={2}
                  maxLength={500}
                  placeholder="Ex: Mensalidade do plano OwlTask Pro - Plataforma SaaS"
                  value={data.service_description ?? ''}
                  onChange={(e) => onPatch({ service_description: e.target.value })}
                />
              )}
            </Field>
          )}
        </div>
      </fieldset>

      <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
        <PillButton type="button" onClick={onCancel}>
          Cancelar
        </PillButton>
        <PillButton solid type="button" onClick={onNext} disabled={!canAdvance}>
          Avançar →
        </PillButton>
      </div>
    </div>
  );
}
